import React, { PropsWithChildren } from "react";
import {
  DataLoadingWrapper,
  DataLoadingWrapperProps,
} from "@jugl-web/ui-components";
import { cx } from "@jugl-web/utils";
import { CRMStatus, CRMStatusPill } from "../CRMStatusPill";

export interface CRMInfoSectionItem {
  icon?: React.ReactNode;
  title?: string;
  status?: CRMStatus;
  description?: React.ReactNode;
  tips?: string[];
  onClick?: () => void;
}

interface CRMInfoSectionProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  dataLoadingWrapper?: DataLoadingWrapperProps;
  items?: CRMInfoSectionItem[];
}

export const CRMInfoSection: React.FC<
  PropsWithChildren<CRMInfoSectionProps>
> = ({ title, icon, subtitle, dataLoadingWrapper, items, children }) => {
  return (
    <div className="flex flex-col rounded-3xl border border-solid border-dark-100 bg-white p-8 shadow-[0_2px_4px_rgba(0,0,0,0.08)]">
      <DataLoadingWrapper {...dataLoadingWrapper}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              {icon}
              <span className="font-secondary text-lg font-medium text-black">
                {title}
              </span>
            </div>
            {subtitle && (
              <span className="font-secondary text-sm leading-[140%] tracking-[0.14px] text-dark-800">
                {subtitle}
              </span>
            )}
          </div>
          {items && (
            <div>
              {items.map((item, index) => (
                <React.Fragment key={+index}>
                  <div
                    className={cx("flex w-full flex-col px-6 pb-6", {
                      "pt-6": !!item.title,
                      "cursor-pointer rounded-2xl transition-all hover:bg-[#FBFBFB]":
                        !!item.onClick,
                    })}
                    onClick={item.onClick}
                  >
                    {item.title && (
                      <div className="flex items-center gap-3">
                        {item.icon}
                        <span className="font-secondary leading-[140%] font-medium tracking-[0.16px] text-dark italic">
                          {item.title}
                        </span>
                        {item.status && (
                          <CRMStatusPill status={item.status} size="sm" />
                        )}
                      </div>
                    )}
                    {item.description && (
                      <span className="mt-1.5 max-w-[700px] font-secondary text-[13px] leading-[150%] tracking-[0.13px] text-dark-700">
                        {item.description}
                      </span>
                    )}
                    {item.tips && (
                      <ul className="m-0 mt-1.5 space-y-1.5 pl-5 font-secondary text-sm leading-[140%] tracking-[0.14px] text-dark-700">
                        {item.tips.map((tip, idx) => (
                          <li key={+idx}>{tip}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {index !== items.length - 1 && (
                    <div className="my-2 h-px w-full bg-[#F2F2F2]" />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
          {children}
        </div>
      </DataLoadingWrapper>
    </div>
  );
};
